import { useCallback, useEffect, useRef, useState } from "react";
import { ApiClient, ApiResult } from "../api/apiClient";
import { useApiClient } from "./useApiClient";
import { useDidUnmount } from "./useDidUnmount";

export type ApiRequestState<T> = {
  data: T | null;
  isLoading: boolean;
  error: string | null;
  statusCode: number | null;
  continuationToken?: string;
  result: ApiResult<T> | null;
};

export const useApiRequest = <T>(
  fn: (apiClient: ApiClient) => Promise<ApiResult<T>> | null,
  deps: any[]
) => {
  const didUnmount = useDidUnmount();
  const apiClient = useApiClient();
  // Only the latest request may update state, so a slower earlier response can't overwrite a newer one.
  const requestIdRef = useRef(0);
  const [refreshCount, setRefreshCount] = useState<number>(0);

  const refresh = useCallback(
    () => setRefreshCount((state) => state + 1),
    [setRefreshCount]
  );

  const [apiState, setApiState] = useState<ApiRequestState<T>>({
    data: null,
    isLoading: true,
    error: null,
    statusCode: null,
    result: null,
  });

  useEffect(() => {
    setApiState((state) => ({ ...state, isLoading: true }));
    const requestPromise = fn(apiClient);

    if (!requestPromise) {
      return;
    }
    // Only bumped when a request is issued, so a null request keeps the previous behaviour.
    const requestId = ++requestIdRef.current;

    requestPromise
      .then((result) => {
        if (didUnmount.current || requestId !== requestIdRef.current) {
          return;
        }
        if (result.ok) {
          setApiState({
            isLoading: false,
            data: result.data,
            error: null,
            statusCode: result.statusCode,
            continuationToken: result.continuationToken,
            result,
          });
        } else {
          setApiState({
            isLoading: false,
            data: null,
            error: `HTTP ${result.statusCode} error : ${result.message}`,
            statusCode: result.statusCode ?? null,
            result,
          });
        }
      })
      .catch((error) => {
        if (didUnmount.current || requestId !== requestIdRef.current) {
          return;
        }
        setApiState({
          isLoading: false,
          data: null,
          error: "Unknown error.",
          statusCode: null,
          result: null,
        });
      });
  }, [didUnmount, apiClient, setApiState, refreshCount, ...deps]);

  return { ...apiState, refresh };
};
